import type { PlacedBlockWire, PlayerWire, ServerMessage } from "./types";

export class NetworkClient {
  private socket: WebSocket | null = null;
  private handler: ((message: ServerMessage) => void) | null = null;
  private lastState = 0;
  private pingSent = 0;
  rttMs = 0;
  id = "";

  setHandler(handler: (message: ServerMessage) => void): void {
    this.handler = handler;
  }

  async connect(username: string): Promise<void> {
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${protocol}//${location.host}/ws`;
    await new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(url);
      this.socket = socket;
      const timeout = window.setTimeout(() => reject(new Error("WebSocket timeout")), 8_000);
      socket.addEventListener("open", () => socket.send(JSON.stringify({ type: "hello", username })));
      socket.addEventListener("message", (event) => {
        const message = JSON.parse(String(event.data)) as ServerMessage;
        if (message.type === "welcome") {
          this.id = message.id;
          window.clearTimeout(timeout);
          resolve();
        }
        if (message.type === "pong" && typeof message.sent === "number") this.rttMs = performance.now() - message.sent;
        this.handler?.(message);
      });
      socket.addEventListener("error", () => reject(new Error("WebSocket connection failed")));
      socket.addEventListener("close", () => {
        window.clearTimeout(timeout);
      });
    });
  }

  update(now: number, state: Omit<PlayerWire, "id" | "username" | "updatedAt">): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    if (now - this.lastState >= 100) {
      this.lastState = now;
      this.socket.send(JSON.stringify({ type: "state", ...state }));
    }
    if (now - this.pingSent >= 2_000) {
      this.pingSent = now;
      this.socket.send(JSON.stringify({ type: "ping", sent: now }));
    }
  }

  putBlock(block: PlacedBlockWire): void {
    this.send({ type: "block_put", block });
  }

  removeBlock(id: string): void {
    this.send({ type: "block_remove", id });
  }

  private send(payload: object): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(payload));
  }
}
